import { useState } from "react";
import { Wand2, Copy, Check, Lock, ArrowLeft, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { aiAPI } from "../../api";
import Chip from "../../components/atoms/Chip";
import Button from "../../components/atoms/Button";

// Same tones the backend offers — kept in sync via /api/ai/rephrase/tones
// but listed here for the empty-state grid before the first call.
const TONE_HINTS = [
  { id: "professional", label: "Professional" },
  { id: "friendly",     label: "Friendly" },
  { id: "polite",       label: "Polite" },
  { id: "confident",    label: "Confident" },
  { id: "concise",      label: "Concise" },
  { id: "corporate",    label: "Corporate" },
  { id: "ielts",        label: "IELTS Band 7+" },
  { id: "american",     label: "American" },
  { id: "british",      label: "British" },
  { id: "fluent",       label: "Native fluent" },
];

export default function RephraseStudio({ embedded = false, anonymous = false }) {
  const [text, setText] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [locked, setLocked] = useState([]);
  const [limitInfo, setLimitInfo] = useState(null); // { used, limit, bucket, upgradeUrl, signUpUrl }

  async function runRephrase() {
    if (!text.trim()) { setError("Paste some text first."); return; }
    setError(null);
    setLimitInfo(null);
    setLoading(true);
    setResults(null);
    try {
      const data = await aiAPI.rephrase(text);
      setResults(data.rewrites);
      setLocked(data.locked || []);
    } catch (err) {
      // Rate-limit responses arrive as 429 with structured body
      if (err.message && /Daily limit reached/i.test(err.message)) {
        setLimitInfo({ message: err.message, raw: err });
      } else {
        setError(err.message || "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={embedded ? "" : "page-enter"} style={{ paddingBottom: embedded ? 0 : 64, display: "flex", flexDirection: "column", gap: 24 }}>
      {!embedded && (
        <div>
          <Link to="/write" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--fg-3)", fontSize: 13, fontWeight: 500, textDecoration: "none", marginBottom: 12 }}>
            <ArrowLeft size={14} strokeWidth={1.6} /> Write
          </Link>
          <Chip tone="gold">Rephrase Studio</Chip>
          <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 8px" }}>
            One message, <em style={{ color: "var(--gold-500)" }}>ten ways.</em>
          </h1>
          <p style={{ color: "var(--fg-2)", fontSize: 16, lineHeight: 1.55, margin: 0, maxWidth: "60ch" }}>
            Paste any message. Get it rewritten as professional, polite, confident, IELTS-ready, and seven more — all in one shot.
          </p>
        </div>
      )}

      {/* Input */}
      <div style={{ background: "#fff", border: "1px solid var(--border-1)", borderRadius: "var(--radius-lg)", padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste your message here…&#10;&#10;e.g. sir i waited for your call please reply"
          rows={5}
          style={{
            width: "100%",
            fontFamily: "var(--font-body)",
            fontSize: 15,
            lineHeight: 1.55,
            border: "1px solid var(--border-2)",
            borderRadius: "var(--radius-md)",
            padding: "12px 14px",
            resize: "vertical",
            background: "#fff",
            color: "var(--fg-1)",
            outline: "none",
          }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "var(--fg-3)" }}>{text.length} / 1500</span>
          <Button variant="primary" onClick={runRephrase} disabled={loading || !text.trim()} iconLeft={<Wand2 size={15} strokeWidth={1.8} />}>
            {loading ? "Rewriting…" : anonymous ? "Rewrite (3 free tones)" : "Rewrite in 10 tones"}
          </Button>
        </div>
        {error && <p style={{ color: "var(--berry)", fontSize: 13, margin: 0 }}>{error}</p>}
        {limitInfo && (
          <div style={{ background: "#fdf0f1", border: "1px solid #ddb1b6", borderRadius: "var(--radius-md)", padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <p style={{ color: "var(--berry-dark)", fontSize: 13, margin: 0, fontWeight: 600 }}>{limitInfo.message}</p>
            {anonymous ? (
              <Link to="/signup" style={{ textDecoration: "none" }}>
                <Button variant="primary" size="sm">Sign up free</Button>
              </Link>
            ) : (
              <Link to="/pricing" style={{ textDecoration: "none" }}>
                <Button variant="primary" size="sm" iconLeft={<Crown size={13} strokeWidth={1.8} />}>Upgrade</Button>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Empty state — tone hint grid */}
      {!results && !loading && (
        <div>
          <p className="eyebrow" style={{ marginBottom: 10 }}>You'll get back</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {(anonymous ? TONE_HINTS.slice(0, 3) : TONE_HINTS).map((t) => (
              <Chip key={t.id} tone="neutral">{t.label}</Chip>
            ))}
            {anonymous && TONE_HINTS.slice(3).map((t) => (
              <span key={t.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", background: "var(--paper-deep)", border: "1px solid var(--border-2)", borderRadius: 999, fontSize: 11, fontWeight: 600, color: "var(--fg-3)" }}>
                <Lock size={10} strokeWidth={1.8} /> {t.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "var(--fg-3)" }}>
          <Wand2 size={28} strokeWidth={1.4} style={{ opacity: 0.5, animation: "pulse 1.5s ease-in-out infinite" }} />
          <p style={{ marginTop: 12, fontSize: 14 }}>Rewriting in {anonymous ? "3" : "10"} tones…</p>
        </div>
      )}

      {/* Results */}
      {results && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {results.map((r) => <ResultCard key={r.tone} result={r} />)}

          {/* Locked tones teaser for anonymous users */}
          {anonymous && locked.length > 0 && (
            <div style={{ background: "var(--paper-deep)", border: "1px dashed var(--border-2)", borderRadius: "var(--radius-lg)", padding: 24, textAlign: "center" }}>
              <Lock size={20} strokeWidth={1.6} color="var(--fg-3)" />
              <p className="font-display" style={{ fontSize: 24, lineHeight: 1.15, fontWeight: 400, margin: "10px 0 6px" }}>
                <em style={{ color: "var(--gold-500)" }}>7 more tones</em> unlock with a free account.
              </p>
              <p style={{ color: "var(--fg-2)", fontSize: 14, margin: "0 0 14px" }}>
                {locked.map((l) => l.label).join(" · ")}
              </p>
              <Link to="/signup" style={{ textDecoration: "none" }}>
                <Button variant="primary">Sign up free</Button>
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultCard({ result }) {
  const [copied, setCopied] = useState(false);

  function copy() {
    if (!result.text) return;
    navigator.clipboard?.writeText(result.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <article style={{ background: "#fff", border: "1px solid var(--border-1)", borderTop: "3px solid var(--gold-500)", borderRadius: "var(--radius-lg)", padding: "18px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <p style={{ fontFamily: "var(--font-display)", fontSize: 20, lineHeight: 1.1, margin: 0, fontWeight: 400, color: "var(--fg-1)" }}>{result.label}</p>
          <p style={{ fontSize: 12, color: "var(--fg-3)", margin: "2px 0 0" }}>{result.desc}</p>
        </div>
        <button type="button" onClick={copy} disabled={!result.text} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "6px 12px", fontSize: 12, fontWeight: 600,
          background: copied ? "var(--sage)" : "#fff",
          color: copied ? "var(--paper)" : "var(--fg-2)",
          border: `1px solid ${copied ? "var(--sage)" : "var(--border-2)"}`,
          borderRadius: 999, cursor: result.text ? "pointer" : "not-allowed",
          transition: "all 150ms cubic-bezier(.2,.8,.2,1)",
        }}>
          {copied ? <Check size={13} strokeWidth={2} /> : <Copy size={13} strokeWidth={1.6} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <p style={{ fontSize: 15, lineHeight: 1.6, color: "var(--fg-1)", margin: 0, whiteSpace: "pre-wrap" }}>
        {result.text || <span style={{ color: "var(--berry)", fontSize: 13 }}>Couldn't generate this one — try again.</span>}
      </p>
    </article>
  );
}
